/**
 * Presentation helpers for the Lost Sales revenue estimate.
 *
 * The backend returns `revenue_estimate` per scope (estimated lost units x
 * CURRENT catalog price). Amounts are never combined across currencies and
 * unknown prices are never zero, so every helper here keeps those cases
 * visible instead of collapsing them into a single number.
 */

export const REVENUE_BASIS_LABEL = "at current selling prices";

const formatUnits = (value) =>
  (Math.round((Number(value) || 0) * 10) / 10).toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

export const formatCurrencyAmount = (amount, currency, { compact = false } = {}) => {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const value = Number(amount);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
      ...(compact ? { notation: "compact" } : {}),
    }).format(value);
  } catch (_error) {
    return `${Math.round(value).toLocaleString("en-US")} ${currency || ""}`.trim();
  }
};

/**
 * KPI state for one scope.
 *   none        - the API returned no estimate (older response)
 *   unavailable - no usable price/currency; amount is null (NOT zero)
 *   incomplete  - an amount exists but excludes some unpriced lost units
 *   available   - complete estimate
 */
export const describeRevenueEstimate = (estimate) => {
  if (!estimate) return { state: "none", amountLabel: "—", note: "" };

  const unpriced = Number(estimate.unpriced_lost_units) || 0;
  if (estimate.estimated_lost_revenue == null) {
    const reason =
      estimate.currency_status && estimate.currency_status !== "ok"
        ? "no valid currency on the product"
        : "no usable current price";
    return {
      state: "unavailable",
      amountLabel: "—",
      note: `Unavailable: ${reason}.`,
    };
  }

  const amountLabel = formatCurrencyAmount(
    estimate.estimated_lost_revenue,
    estimate.currency,
  );
  if (!estimate.complete) {
    return {
      state: "incomplete",
      amountLabel,
      note: `Incomplete: excludes ${formatUnits(unpriced)} lost units without a usable price.`,
    };
  }
  return { state: "available", amountLabel, note: "" };
};

/** Same grouping rules as the backend summary, for merged multi-batch results. */
export const summarizeRevenueRows = (succeededRows = []) => {
  const byCurrency = new Map();
  let unpricedLostUnits = 0;
  let incompleteScopes = 0;

  succeededRows.forEach((row) => {
    const estimate = row?.revenue_estimate;
    if (!estimate) return;
    unpricedLostUnits += Number(estimate.unpriced_lost_units) || 0;
    if (!estimate.complete) incompleteScopes += 1;
    if (estimate.estimated_lost_revenue == null || !estimate.currency) return;
    const entry = byCurrency.get(estimate.currency) || {
      currency: estimate.currency,
      estimated_lost_revenue: 0,
      priced_lost_units: 0,
      scopes: 0,
    };
    entry.estimated_lost_revenue += Number(estimate.estimated_lost_revenue);
    entry.priced_lost_units += Number(estimate.priced_lost_units) || 0;
    entry.scopes += 1;
    byCurrency.set(estimate.currency, entry);
  });

  return {
    basis: "current_selling_price",
    by_currency: [...byCurrency.values()].sort((a, b) =>
      a.currency.localeCompare(b.currency),
    ),
    unpriced_lost_units: unpricedLostUnits,
    incomplete_scopes: incompleteScopes,
    complete: incompleteScopes === 0,
  };
};

/** Chips for the bulk summary: one label per currency, never a combined total. */
export const describeBulkRevenue = (revenueSummary) => {
  if (!revenueSummary) return null;
  const totals = (revenueSummary.by_currency || []).map((entry) =>
    formatCurrencyAmount(entry.estimated_lost_revenue, entry.currency),
  );
  return {
    totals,
    multiCurrency: totals.length > 1,
    incomplete: !revenueSummary.complete,
    note: revenueSummary.complete
      ? ""
      : `Incomplete: ${revenueSummary.incomplete_scopes} scope${
          revenueSummary.incomplete_scopes === 1 ? "" : "s"
        } without full pricing (${formatUnits(revenueSummary.unpriced_lost_units)} lost units excluded).`,
  };
};
